import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lightning, Play, Calendar, CaretRight } from "@phosphor-icons/react";
import { formatINR } from "../utils/coursesCheckout";

const API = process.env.REACT_APP_BACKEND_URL;
const AUTO_MS = 5000;

function CourseCarousel({ items, accent, headerIcon, title, subtitle, viewAllTab }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const scrollerRef = useRef(null);
  const isLive = accent === "live";

  // Auto-rotate
  useEffect(() => {
    if (!items.length) return;
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), AUTO_MS);
    return () => clearInterval(t);
  }, [items.length]);

  // Sync scroll on idx change — scroll only the horizontal carousel, never the page
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.children[idx];
    if (card) {
      // Use scrollTo on the scroller (not scrollIntoView, which scrolls the whole page)
      el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
    }
  }, [idx]);

  if (!items.length) return null;

  return (
    <section className="py-6">
      <div className="flex items-end justify-between mb-3 px-4">
        <div>
          <h3 className="text-base md:text-lg font-bold text-[#1F1B13] inline-flex items-center gap-2">
            {headerIcon}
            {title}
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</p>
        </div>
        <button
          onClick={() => navigate(`/courses?tab=${viewAllTab}`)}
          className="text-xs font-semibold text-[#A88838] hover:underline inline-flex items-center gap-0.5"
          data-testid={`courses-viewall-${accent}`}
        >
          View all <CaretRight size={12} weight="bold" />
        </button>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 px-4 scrollbar-hide"
        style={{ scrollbarWidth: "none" }}
      >
        {items.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/courses/${c.handle}`)}
            className="snap-start shrink-0 w-72 h-48 rounded-2xl overflow-hidden relative shadow-md hover:shadow-lg transition group"
            data-testid={`courses-card-${c.handle}`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#A88838] to-[#5C4626]">
              {c.image_url && (
                <img src={c.image_url} alt={c.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition" loading="lazy" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
            </div>
            <span className={`absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider shadow ${isLive ? "bg-red-500 text-white" : "bg-violet-600 text-white"}`}>
              {isLive ? <><Lightning size={10} weight="fill" /> LIVE BATCH</> : <><Play size={10} weight="fill" /> RECORDED</>}
            </span>
            <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
              <p className="text-white font-extrabold text-base leading-snug line-clamp-2 mb-1.5">{c.title}</p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-white text-sm font-bold">{formatINR(c.price)}</span>
                {isLive && c.batch_start_date && (
                  <span className="text-[#FFE8A8] text-[11px] font-semibold inline-flex items-center gap-1">
                    <Calendar size={11} /> {c.batch_start_date}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Dots */}
      <div className="flex items-center justify-center gap-1.5 mt-1">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? (isLive ? "w-6 bg-red-500" : "w-6 bg-violet-600") : "w-1.5 bg-zinc-300"}`}
          />
        ))}
      </div>
    </section>
  );
}

export default function CoursesCarousels() {
  const [live, setLive] = useState([]);
  const [recorded, setRecorded] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/courses/list`)
      .then(r => r.json())
      .then(d => { if (d.success) { setLive(d.live || []); setRecorded(d.recorded || []); } })
      .catch(() => {});
  }, []);

  if (!live.length && !recorded.length) return null;

  return (
    <div className="bg-gradient-to-b from-white to-[#FFFEF8]" data-testid="courses-carousels">
      <div className="max-w-5xl mx-auto">
        {live.length > 0 && (
          <CourseCarousel
            items={live}
            accent="live"
            headerIcon={<span className="text-red-500">🔴</span>}
            title="Live Courses & Batches"
            subtitle="Real-time classes · Limited seats"
            viewAllTab="live"
          />
        )}
        {recorded.length > 0 && (
          <CourseCarousel
            items={recorded}
            accent="recorded"
            headerIcon={<span className="text-violet-600">🎬</span>}
            title="Recorded Courses"
            subtitle="Watch anytime · Lifetime access"
            viewAllTab="recorded"
          />
        )}
      </div>
    </div>
  );
}
